/**
 * Navigation seams of `00-03-EVENEMENT` (DEV-7682).
 *
 * Navigation in this app is hardcoded per screen — `getNextScreen()` /
 * `skipConditions` / `CONDITIONAL_SCREENS` in `src/config/flowDefinitions.ts` are
 * only reachable through `uiStore.getNextScreenCode()`, which has no callers. So
 * the rule cannot live in the flow definition; it has to be ONE shared module
 * consumed by BOTH navigation directions (same approach as
 * `utils/tripPaymentScreenVisibility.ts`, DEV-7479):
 *  - forward:  `LegalNoticeScreen.handleContinue`      (00-02 → 00-03 | 01-00)
 *  - backward: `ContactPreferencesScreen.handleBack`   (01-00 → 00-03 | 00-02)
 *
 * The screen itself is UNCONDITIONAL: every tenant collects the event date, PV
 * included, and there is no display flag. What is resolved here is DRAFT RESUME
 * (ADR-042) and WHERE THE DATE CAME FROM — "has this claim already gone past this
 * point, and is the date still the claimant's to give?":
 *
 *  | state    | meaning                                             | forward | backward |
 *  |----------|-----------------------------------------------------|---------|----------|
 *  | FRESH    | new claim, nothing answered past the entry section  | 00-03   | 00-02    |
 *  | IMPORTED | existing-event import: the BFF seeded the screen    | 00-03   | 00-03    |
 *  |          | from NOVA's Incident_Date, claimant not through yet  |         |          |
 *  | ANSWERED | `stepData['00-03-EVENEMENT']` holds a date          | 01-00   | 00-03    |
 *  | LEGACY   | draft started before this story: progress past the  | 01-00   | 00-02    |
 *  |          | entry section but no event-date key                 |         |          |
 *
 * ANSWERED is skipped going FORWARD (ADR-042: never re-ask an answered screen on
 * resume) but shown going BACKWARD, which is what keeps the date editable. That
 * asymmetry is safe: the screen is reachable in that state and prefilled. LEGACY
 * is skipped in BOTH directions, so a draft created before this story never hits
 * a blocking empty screen — and nothing here ever deletes data: resolving a state
 * is pure, no `clearScreenData` / flow cleanup is triggered for a legacy draft.
 *
 * IMPORTED is why LEGACY could not simply be reused for an existing-event import.
 * An import arrives with sections `01`+ already populated from NOVA, so
 * `hasProgressPastEntrySection` read it as progress and classified it LEGACY —
 * skipping the date screen in both directions on a claim that is in fact BRAND NEW.
 * Imported data is not claimant progress, and the BFF says so explicitly
 * (`source: 'NOVA_EVENT_IMPORT'`). The screen is therefore SHOWN, and shown
 * read-only: see `utils/eventDateReminder.ts` for why the date cannot be retyped.
 */

export const LEGAL_NOTICE_SCREEN_CODE = '00-02-AVIS';
export const EVENT_DATE_SCREEN_CODE = '00-03-EVENEMENT';
export const CLAIMANT_SCREEN_CODE = '01-00-DEMANDEUR';

export const LEGAL_NOTICE_ROUTE = `/claim/${LEGAL_NOTICE_SCREEN_CODE}`;
export const EVENT_DATE_ROUTE = `/claim/${EVENT_DATE_SCREEN_CODE}`;
export const CLAIMANT_ROUTE = `/claim/${CLAIMANT_SCREEN_CODE}`;

/**
 * Value of `stepData['00-03-EVENEMENT'].source` written by the BFF when the claim was
 * created from an event that already exists in NOVA (`NovaToPortalMapper
 * .mapForExistingEventImport`). The date is then NOVA's `Incident_Date`, not an answer.
 *
 * Must match `NovaToPortalMapper.EVENT_DATE_SOURCE_NOVA_IMPORT` byte for byte — it is a
 * wire contract, so a rename on either side is a breaking change on both.
 */
export const NOVA_EVENT_IMPORT_SOURCE = 'NOVA_EVENT_IMPORT';

/** Entry-section screens (`00-*`) — they carry no claim answers of their own. */
const ENTRY_SECTION_PREFIX = '00-';

/** `stepData['00-03-EVENEMENT']` as this module and the screen read it. */
export interface EventDateScreenData {
  eventDate?: string | null;
  /** Written by `EventDateScreen.saveAndContinue` — the claimant walked THROUGH the screen. */
  eventDateComplete?: boolean;
  /** Provenance of the date; `NOVA_EVENT_IMPORT` when seeded from an existing event. */
  source?: string | null;
}

/**
 * Minimal shape read out of `claimStore.stepData`.
 * Deliberately loose on the sibling keys (the store itself is `Record<string, any>`),
 * but the one key this module owns is typed.
 */
export interface EventDateStepData {
  [screenCode: string]: unknown;
  '00-03-EVENEMENT'?: EventDateScreenData | undefined;
}

export type EventDateResumeState = 'FRESH' | 'IMPORTED' | 'ANSWERED' | 'LEGACY';

/** A stepData entry counts as real progress only when it actually holds something. */
const hasContent = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

/** Has the event date already been answered on this claim? */
export const hasAnsweredEventDate = (
  stepData: EventDateStepData | null | undefined
): boolean => hasContent(stepData?.[EVENT_DATE_SCREEN_CODE]?.eventDate);

/**
 * The answered event date, or `undefined` when this claim carries none.
 *
 * The single reader of `stepData['00-03-EVENEMENT'].eventDate` outside the screen that
 * writes it. `undefined` (never `null`, never `''`) is what lets the eligibility request
 * OMIT the key entirely for a legacy draft — see `services/api/endpoints/eligibilityEndpoints.ts`.
 */
export const getAnsweredEventDate = (
  stepData: EventDateStepData | null | undefined
): string | undefined => {
  const eventDate = stepData?.[EVENT_DATE_SCREEN_CODE]?.eventDate;
  return typeof eventDate === 'string' && eventDate.trim() !== '' ? eventDate : undefined;
};

/**
 * Was this claim's event date seeded from an event that already exists in NOVA?
 *
 * True for the whole life of the claim — the marker is never cleared, because the date
 * never stops belonging to the assistance case. That is what keeps the field locked on a
 * draft resume, not only on the first pass.
 */
export const isImportedEventDate = (
  stepData: EventDateStepData | null | undefined
): boolean => stepData?.[EVENT_DATE_SCREEN_CODE]?.source === NOVA_EVENT_IMPORT_SOURCE;

/**
 * Is this an import the claimant has not walked through yet?
 *
 * The BFF seeds the date but deliberately never writes `eventDateComplete`, so this is
 * what tells "just imported, screen still owed" from "imported and already acknowledged".
 * Only the first is forced through the screen; afterwards the claim navigates like any
 * ANSWERED one, which is what keeps ADR-042 intact for a resumed import.
 */
export const isPendingImportedEventDate = (
  stepData: EventDateStepData | null | undefined
): boolean =>
  isImportedEventDate(stepData) &&
  stepData?.[EVENT_DATE_SCREEN_CODE]?.eventDateComplete !== true;

/**
 * Did this claim already progress past the entry section (`00-*`)?
 * That is the signature of a resumed draft, as opposed to a claim that has just
 * been initialized (which only carries its `00-02-AVIS` bootstrap entry).
 */
export const hasProgressPastEntrySection = (
  stepData: EventDateStepData | null | undefined
): boolean =>
  Object.entries(stepData ?? {}).some(
    ([screenCode, value]) => !screenCode.startsWith(ENTRY_SECTION_PREFIX) && hasContent(value)
  );

/**
 * Classify the claim for the two seams below. Pure: never mutates or purges
 * `stepData`.
 *
 * IMPORTED is tested FIRST, and deliberately so: an import carries sections `01`+ from
 * NOVA, so both later branches already match it — ANSWERED through the seeded date,
 * LEGACY through the imported progress — and both of those skip the screen going forward.
 */
export const getEventDateResumeState = (
  stepData: EventDateStepData | null | undefined
): EventDateResumeState => {
  if (isPendingImportedEventDate(stepData)) return 'IMPORTED';
  if (hasAnsweredEventDate(stepData)) return 'ANSWERED';
  if (hasProgressPastEntrySection(stepData)) return 'LEGACY';
  return 'FRESH';
};

/**
 * Forward seam — route to leave `00-02-AVIS` for.
 * A FRESH claim is asked the question and an IMPORTED one is shown the date NOVA already
 * holds; an answered or legacy draft goes straight to the claimant screen.
 */
export const getScreenAfterLegalNotice = (
  stepData: EventDateStepData | null | undefined
): string => {
  const state = getEventDateResumeState(stepData);

  return state === 'FRESH' || state === 'IMPORTED' ? EVENT_DATE_ROUTE : CLAIMANT_ROUTE;
};

/**
 * Backward seam — route to leave `01-00-DEMANDEUR` for.
 * An ANSWERED claim goes back to the event date (so it stays editable), and an IMPORTED
 * one goes back to it as well — read-only, but it IS the screen that precedes the
 * claimant, and sending them to the legal notice instead would skip a screen they just
 * saw. A legacy draft — which never saw the screen — goes back to the legal notice,
 * exactly as before this story.
 */
export const getScreenBeforeClaimant = (
  stepData: EventDateStepData | null | undefined
): string => {
  const state = getEventDateResumeState(stepData);

  return state === 'ANSWERED' || state === 'IMPORTED' ? EVENT_DATE_ROUTE : LEGAL_NOTICE_ROUTE;
};
